import React from 'react';
import { Car, Calculator, Gauge, Users, MapPin } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров ОСАГО
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  popularRegions
}) => {
  // Обработчик изменения мощности двигателя
  const handleEnginePowerChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      enginePower: newValue
    }));
  };
  
  // Обработчик изменения возраста водителя
  const handleDriverAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      driverAge: newValue,
      // Обновляем первого водителя, если он существует
      drivers: prev.drivers.length > 0 
        ? prev.drivers.map((driver, idx) => idx === 0 ? { ...driver, age: newValue } : driver) 
        : prev.drivers
    }));
  };
  
  // Обработчик изменения стажа вождения
  const handleDriverExperienceChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      drivingExperience: newValue,
      // Обновляем первого водителя, если он существует
      drivers: prev.drivers.length > 0 
        ? prev.drivers.map((driver, idx) => idx === 0 ? { ...driver, experience: newValue } : driver) 
        : prev.drivers
    }));
  };
  
  // Функция расчета
  const calculateInsurance = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Тип транспортного средства */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Тип транспортного средства</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, carType: 'passenger' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'passenger' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium flex items-center justify-center">
                <Car className="w-4 h-4 mr-1.5" /> Легковой
              </div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, carType: 'motorcycle' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'motorcycle' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мотоцикл</div>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, carType: 'truck' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'truck' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Грузовой</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, carType: 'bus' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.carType === 'bus' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Автобус</div>
            </button>
          </div>
        </div>
        
        {/* Мощность двигателя */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="enginePowerSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Gauge className="w-4 h-4 mr-1.5 text-emerald-500" /> Мощность двигателя
            </label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.enginePower} л.с.</span>
          </div>
          <input
            type="range"
            id="enginePowerSlider"
            min="50"
            max="300"
            step="10"
            value={formData.enginePower}
            onChange={handleEnginePowerChange}
            aria-label="Мощность двигателя (л.с.)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными значениями мощности */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {['75', '100', '150', '200'].map(power => (
              <button
                key={power}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, enginePower: power }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.enginePower === power 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {power} л.с.
              </button>
            ))}
          </div>
        </div>
        
        {/* Регион */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400 flex items-center">
              <MapPin className="w-4 h-4 mr-1.5 text-emerald-500" /> Популярные регионы
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {popularRegions.slice(0, 4).map(region => (
              <button
                key={region.code}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, regionCode: region.code }))}
                className={`px-2 py-1.5 text-xs rounded border transition-colors
                  ${formData.regionCode === region.code 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {region.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Водитель */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label className="text-gray-600 dark:text-gray-400 flex items-center">
              <Users className="w-4 h-4 mr-1.5 text-emerald-500" /> Параметры водителя
            </label>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-2">
            <div>
              <label htmlFor="driverAgeSlider" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Возраст: {formData.driverAge} лет
              </label>
              <input
                type="range"
                id="driverAgeSlider"
                min="18"
                max="80"
                step="1"
                value={formData.driverAge}
                onChange={handleDriverAgeChange}
                aria-label="Возраст водителя"
                className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                disabled={formData.multiDrive}
              />
            </div>
            <div>
              <label htmlFor="driverExperienceSlider" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                Стаж: {formData.drivingExperience} лет
              </label>
              <input
                type="range"
                id="driverExperienceSlider"
                min="0"
                max={Math.min(formData.driverAge - 18, 60)}
                step="1"
                value={formData.drivingExperience}
                onChange={handleDriverExperienceChange}
                aria-label="Стаж водителя"
                className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                disabled={formData.multiDrive}
              />
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, multiDrive: !prev.multiDrive }))}
            className={`w-full py-1.5 text-sm rounded border transition-colors flex items-center justify-center
              ${formData.multiDrive 
                ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
              }`}
          >
            <Users className="w-4 h-4 mr-1.5" />
            {formData.multiDrive ? 'Мультидрайв (включен)' : 'Включить мультидрайв'}
          </button>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateInsurance}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать ОСАГО
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;